// Client admin-api bertipe dari kontrak `@folaplus/contracts` (sumber yang sama dengan OpenAPI admin).
// Sesi = cookie HttpOnly `fp_admin` (path /admin); setiap request membawa header CSRF.
import type { AdminRouteName, AdminRoutes } from '@folaplus/contracts';
import { adminRouteMeta, type RouteMeta } from '@folaplus/contracts/route-meta';
import type { z } from 'zod';

type RouteOf<N extends AdminRouteName> = AdminRoutes[N];
export type ResponseOf<N extends AdminRouteName> = z.output<RouteOf<N>['response']>;
export type BodyOf<N extends AdminRouteName> =
  RouteOf<N> extends { body: infer B extends z.ZodType } ? z.input<B> : never;
export type QueryOf<N extends AdminRouteName> =
  RouteOf<N> extends { query: infer Q extends z.ZodType } ? z.input<Q> : never;

type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;
type ParamsOf<N extends AdminRouteName> = [ParamNames<RouteOf<N>['path']>] extends [never]
  ? { params?: never }
  : { params: Record<ParamNames<RouteOf<N>['path']>, string> };

export type CallArgs<N extends AdminRouteName> = ParamsOf<N> & {
  body?: BodyOf<N>;
  query?: QueryOf<N> | Record<string, string | undefined>;
  file?: File;
  /** Field teks tambahan di form multipart bersama `file` (mis. `reason` unggah QRIS). */
  fields?: Record<string, string>;
};

/**
 * Error API (problem+json); `message` dalam Bahasa Indonesia dari server. `details` = isi mentah problem+json
 * (mis. `{ nextChangeAt }` pada REFERRAL_CODE_COOLDOWN).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors: Record<string, string> = {},
    readonly details: unknown = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Terjadi kesalahan. Silakan coba lagi.';
}

/** Dipanggil saat sesi admin berakhir (401) agar aplikasi kembali ke layar masuk. */
let onUnauthenticated: () => void = () => undefined;
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

function buildUrl(path: string, params?: Record<string, string>, query?: unknown): string {
  const filled = path.replace(/:(\w+)/g, (_m, key: string) => encodeURIComponent(params?.[key] ?? ''));
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(
    (query ?? {}) as Record<string, string | number | boolean | undefined>,
  )) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${filled}?${qs}` : filled;
}

interface ProblemBody {
  title?: unknown;
  code?: unknown;
  details?: unknown;
}

async function toApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ProblemBody | null;
  if (!body || typeof body.title !== 'string' || typeof body.code !== 'string') {
    return new ApiError(
      response.status,
      'HTTP_ERROR',
      `Server mengembalikan kesalahan (${response.status}).`,
    );
  }
  const fieldErrors: Record<string, string> = {};
  if (Array.isArray(body.details)) {
    for (const detail of body.details as { path?: unknown; message?: unknown }[]) {
      if (
        typeof detail.path === 'string' &&
        typeof detail.message === 'string' &&
        !fieldErrors[detail.path]
      ) {
        fieldErrors[detail.path] = detail.message;
      }
    }
  }
  return new ApiError(response.status, body.code, body.title, fieldErrors, body.details);
}

export async function api<N extends AdminRouteName>(name: N, args: CallArgs<N> = {} as CallArgs<N>) {
  const route: RouteMeta = adminRouteMeta[name];
  const url = buildUrl(route.path, args.params, args.query);
  const headers: Record<string, string> = { 'X-Requested-With': 'folaplus' };
  let body: BodyInit | undefined;
  if (args.file) {
    const form = new FormData();
    form.append('file', args.file);
    for (const [key, value] of Object.entries(args.fields ?? {})) form.append(key, value);
    body = form;
  } else if (route.method !== 'GET' && route.method !== 'DELETE') {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(args.body ?? {});
  }
  let response: Response;
  try {
    response = await fetch(url, { method: route.method, headers, body, credentials: 'same-origin' });
  } catch {
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.',
    );
  }
  if (!response.ok) {
    if (response.status === 401 && route.auth) onUnauthenticated();
    throw await toApiError(response);
  }
  if (response.status === 204) return null as ResponseOf<N>;
  return (await response.json()) as ResponseOf<N>;
}
